import React, { useEffect, useMemo, useState } from "react";
import {
    Card,
    Typography,
    DatePicker,
    Row,
    Col,
    Button,
    Divider,
    Spin,
    Alert,
} from "antd";
import {
    Layers,
    TrendingUp,
    ShoppingBag,
    Activity,
    SlidersHorizontal,
    RefreshCw,
    CalendarClock,
    ArrowLeftRight,
} from "lucide-react";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import {
    getOtherProjectsDate,
    setOtherProjectsDate,
} from "../../redux/otherProjectsSlice/otherProjectsSlice";


const { Title, Text } = Typography;
const DATE_FORMAT = "DD-MM-YYYY";

const CONFIG_TYPES = [
    {
        id: "other-projects",
        name: "Other Projects",
        icon: Layers,
        type: "other",
    },
    {
        id: "anticipated-order",
        name: "Anticipated Order",
        icon: Activity,
        type: "anticipated",
    },
    {
        id: "direct-orders",
        name: "Direct Orders",
        icon: ShoppingBag,
        type: "direct",
    },
    {
        id: "iut",
        name: "IUT",
        icon: ArrowLeftRight,
        type: "iut",
    },
    {
        id: "operating-performance",
        name: "Operating Performance",
        icon: TrendingUp,
        type: "operating",
    },
    {
        id: "system-date",
        name: "System Date",
        icon: CalendarClock,
        type: "system",
        startDateOnly: true,
    },
];

export default function OtherProjectsDate() {
    const dispatch = useDispatch();

    const {
        otherProjectsDate,
        loading,
        settingOtherProjectsDate,
    } = useSelector((state) => state.otherProjects);

    const [formData, setFormData] = useState({
        STARTDATE: "",
        ENDDATE: "",
    });
    const [validationError, setValidationError] = useState("");

    const [activeTab, setActiveTab] = useState("other-projects");
    const activeConfig = CONFIG_TYPES.find(
        (item) => item.id === activeTab
    );

    useEffect(() => {
        dispatch(getOtherProjectsDate(activeConfig.type));
        setValidationError("");
    }, [dispatch, activeConfig]);

    useEffect(() => {
        setFormData({
            STARTDATE: otherProjectsDate?.STARTDATE || "",
            ENDDATE: otherProjectsDate?.ENDDATE || "",
        });

        if (activeConfig.type === "system" && otherProjectsDate?.STARTDATE) {
            const date = dayjs(otherProjectsDate.STARTDATE, DATE_FORMAT);
            if (date.isValid() && date.month() !== 3) {
                setValidationError("System Date month must be April.");
                return;
            }
        }
        setValidationError("");
    }, [otherProjectsDate, activeConfig.type]);

    const isDirty = useMemo(
        () =>
            formData.STARTDATE !== (otherProjectsDate?.STARTDATE || "") ||
            formData.ENDDATE !== (otherProjectsDate?.ENDDATE || ""),
        [formData, otherProjectsDate]
    );

    const handleSubmit = () => {
        dispatch(
            setOtherProjectsDate({
                ...formData,
                type: activeConfig.type,
                configName: activeConfig.name,
            })
        ).then(() =>
            dispatch(getOtherProjectsDate(activeConfig.type))
        );
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-[70vh]">
                <Spin size="large" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-50 px-8 py-6">

            {/* Header */}

            <div className="mb-8">
                <Title
                    level={2}
                    style={{ marginBottom: 6 }}
                    className="!flex !items-center gap-2"
                >
                    <SlidersHorizontal size={38} className="text-blue-600 bg-blue-100 rounded-md p-1 " />
                    Date Configuration
                </Title>

                <Text type="secondary">
                    Configure financial and operational date ranges across different
                    modules.
                </Text>
            </div>

            {/* Tabs */}

            <div className="mb-8 border-b border-slate-200">
                <div className="flex gap-1 overflow-x-auto">

                    {CONFIG_TYPES.map((tab) => {

                        const Icon = tab.icon;
                        const active = activeTab === tab.id;

                        return (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`flex items-center gap-2 px-5 py-3 border-b-2 text-sm font-medium transition

                            ${active
                                        ? "border-blue-600 text-blue-600 bg-blue-50 rounded-t-lg"
                                        : "border-transparent text-slate-500 hover:text-slate-700"
                                    }
                        `}
                            >
                                <Icon size={18} />
                                {tab.name}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Current Configuration */}

            <Card
                bordered
                className="mb-6 rounded-xl shadow-sm"
            >
                <div className="flex items-center gap-3 mb-5">

                    <Title level={4} style={{ margin: 0 }}>
                        Current Configuration
                    </Title>

                    <span className="px-3 py-1 rounded-full bg-slate-100 text-xs capitalize">
                        {
                            activeConfig.name
                        }
                    </span>

                </div>

                <Row gutter={20}>

                    <Col xs={24} md={activeConfig.startDateOnly ? 24 : 12}>
                        <div className="border rounded-lg bg-slate-50 p-5">

                            <Text className="uppercase text-xs text-slate-400">
                                Current Start Date
                            </Text>

                            <Title level={3} style={{ marginTop: 10 }}>
                                {otherProjectsDate?.STARTDATE || "--"}
                            </Title>

                        </div>
                    </Col>

                    {!activeConfig.startDateOnly && (
                        <Col xs={24} md={12}>
                            <div className="border rounded-lg bg-slate-50 p-5">

                                <Text className="uppercase text-xs text-slate-400">
                                    Current End Date
                                </Text>

                                <Title level={3} style={{ marginTop: 10 }}>
                                    {otherProjectsDate?.ENDDATE || "--"}
                                </Title>

                            </div>
                        </Col>
                    )}

                </Row>

                <div className="mt-5 text-xs text-slate-500 flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-green-500"></span>
                    Last updated after successful configuration.
                </div>

            </Card>

            {/* Update Configuration */}

            <Card
                bordered
                className="rounded-xl shadow-sm"
            >

                <Title level={4}>
                    Update Configuration
                </Title>

                <Text type="secondary">
                    {activeConfig.name === "System Date" ? "Select the start dates applicable for System Date" : `Select the start and end dates applicable for ${activeConfig.name}`}
                </Text>

                <Divider />

                <Row gutter={24}>

                    <Col xs={24} md={activeConfig.startDateOnly ? 24 : 12}>

                        <Text strong>Start Date</Text>

                        <DatePicker
                            size="large"
                            className="w-full mt-2"
                            format={DATE_FORMAT}
                            disabledDate={(current) => {
                                if (activeConfig.type === "system" && current) {
                                    return current.month() !== 3; // 3 represents April (0-indexed)
                                }
                                return false;
                            }}
                            value={
                                formData.STARTDATE
                                    ? dayjs(
                                        formData.STARTDATE,
                                        DATE_FORMAT
                                    )
                                    : null
                            }
                            onChange={(_, value) => {
                                setFormData((prev) => ({
                                    ...prev,
                                    STARTDATE: value,
                                }));

                                if (activeConfig.type === "system" && value) {
                                    const parsed = dayjs(value, DATE_FORMAT);
                                    if (parsed.isValid() && parsed.month() !== 3) {
                                        setValidationError("System Date month must be April.");
                                        return;
                                    }
                                }
                                setValidationError("");
                            }}
                        />
                        {validationError && (
                            <div className="text-red-500 text-sm mt-2 font-medium">
                                {validationError}
                            </div>
                        )}

                    </Col>

                    {!activeConfig.startDateOnly && (
                        <Col xs={24} md={12}>

                            <Text strong>End Date</Text>

                            <DatePicker
                                size="large"
                                className="w-full mt-2"
                                format={DATE_FORMAT}
                                value={
                                    formData.ENDDATE
                                        ? dayjs(
                                            formData.ENDDATE,
                                            DATE_FORMAT
                                        )
                                        : null
                                }
                                onChange={(_, value) =>
                                    setFormData((prev) => ({
                                        ...prev,
                                        ENDDATE: value,
                                    }))
                                }
                            />

                        </Col>
                    )}

                </Row>

                <Divider />

                <div className="flex justify-end gap-3">

                    <Button
                        size="large"
                        disabled={!isDirty}
                        onClick={() =>
                            setFormData({
                                STARTDATE:
                                    otherProjectsDate?.STARTDATE || "",
                                ENDDATE:
                                    otherProjectsDate?.ENDDATE || "",
                            })
                        }
                    >
                        Reset
                    </Button>

                    <Button
                        size="large"
                        type="primary"
                        loading={settingOtherProjectsDate}
                        disabled={!isDirty || !!validationError}
                        onClick={handleSubmit}
                        icon={<RefreshCw size={16} />}
                    >
                        Update Configuration
                    </Button>

                </div>

            </Card>

        </div>
    );
}
